import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';
import { createOrder, verifyPaymentSignature } from '../store/orderSlice';
import './CheckoutPage.css';

const CheckoutPage = () => {
    const dispatch = useDispatch();
    const location = useLocation(); 
    
    const cart = useSelector(state => state.cart);
    const authUser = useSelector(state => state.auth?.user || state.user?.user); 
    const { error, loading } = useSelector(state => state.orders || {});
    
    const finalGrandTotal = location.state?.checkoutTotal ?? cart.grandTotal ?? 0;

    const [paymentMethod, setPaymentMethod] = useState('RAZORPAY');
    const [isProcessing, setIsProcessing] = useState(false); 

    const [addressForm, setAddressForm] = useState({
        name: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: 'New Ashok Nagar', 
        state: 'Delhi',
        pincode: '',
        address_type: 'home'
    });

    if (!cart.items || cart.items.length === 0) {
        return <Navigate to="/cart" replace />;
    }

    const handleInputChange = (e, field) => {
        setAddressForm({ ...addressForm, [field]: e.target.value });
    };

    const loadRazorpaySDK = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleCheckoutSubmit = async (e) => {
        e.preventDefault();
        
        if (isProcessing || loading || cart.items.length === 0) return; 
        
        setIsProcessing(true);
        const totalPayableAmount = finalGrandTotal;

        // 1. Order Creation Call
        const orderResult = await dispatch(createOrder({
            user_id: authUser?.id || 1,
            payment_method: paymentMethod,
            coupon_code: cart.coupon,
            amount: totalPayableAmount, 
            address_name: addressForm.name,
            address_phone: addressForm.phone,
            address_line1: addressForm.address_line1,
            address_line2: addressForm.address_line2,
            city: addressForm.city,
            state: addressForm.state,
            pincode: addressForm.pincode,
            address_type: addressForm.address_type
        }));

        console.log("Backend Order Payload:", orderResult.payload);

        if (createOrder.fulfilled.match(orderResult)) {
            // PHP Backend से मिले variables Destructure करें
            const { razorpay_order_id, amount, order_id } = orderResult.payload;

            if (paymentMethod === 'RAZORPAY') {
                const sdkLoaded = await loadRazorpaySDK();
                if (!sdkLoaded) {
                    alert('Razorpay SDK Connection failed. Please check your internet connection.');
                    setIsProcessing(false);
                    return;
                }

                // 🎯 CRITICAL FIX: Backend response amount (in paise) passed directly!
                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_THo7vep3LWT8dt', 
                    amount: amount ? Number(amount) : Math.round(Number(totalPayableAmount) * 100), // ✅ Backend Paise Amount
                    currency: 'INR',
                    name: 'RBuyMart E-Commerce',
                    description: `Order #${order_id}`,
                    order_id: razorpay_order_id,
                    prefill: {
                        name: addressForm.name || authUser?.name || 'Customer',
                        email: authUser?.email || 'customer@rbuy.com',
                        contact: addressForm.phone || '9999999999'
                    },
                    handler: async function (response) {
                        const verifyResult = await dispatch(verifyPaymentSignature({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        }));
                        
                        if (verifyPaymentSignature.fulfilled.match(verifyResult)) {
                            window.location.href = `/order-success?id=${order_id}`;
                        } else {
                            alert('Payment Signature Verification Failed.');
                            setIsProcessing(false);
                        }
                    },
                    modal: {
                        ondismiss: function () {
                            setIsProcessing(false); 
                        }
                    },
                    theme: { color: '#0071e3' }
                };

                const rzpWidget = new window.Razorpay(options);

                rzpWidget.on('payment.failed', function (response) {
                    console.error("Razorpay Failure Log:", response.error);
                    alert(`Payment Failed: ${response.error.description || 'Transaction declined'}`);
                    setIsProcessing(false);
                });

                rzpWidget.open();
            } else {
                // COD Flow
                window.location.href = `/order-success?id=${order_id}`;
            }
        } else {
            setIsProcessing(false); 
        }
    };

    return (
        <div className="checkout-container">
            <h1 className="checkout-title">Checkout</h1>
            <form onSubmit={handleCheckoutSubmit} className="checkout-grid">
                <div className="checkout-billing-form">
                    <div className="checkout-block-card">
                        <h3>1. Settlement Infrastructure Options</h3>
                        <div className="payment-options-group">
                            <label className="radio-label-block">
                                <input 
                                    type="radio" 
                                    value="RAZORPAY" 
                                    checked={paymentMethod === 'RAZORPAY'} 
                                    onChange={(e) => setPaymentMethod(e.target.value)} 
                                />
                                <span>UPI / Cards</span>
                            </label>
                            <label className="radio-label-block">
                                <input 
                                    type="radio" 
                                    value="COD" 
                                    checked={paymentMethod === 'COD'} 
                                    onChange={(e) => setPaymentMethod(e.target.value)} 
                                />
                                <span>Cash on Delivery</span>
                            </label>
                        </div>
                        <br />
                        
                        <h3>2. Billing / Shipping Address</h3>
                        <div className="formlabeldiv">
                            <input type="text" required value={addressForm.name} onChange={(e) => handleInputChange(e, 'name')} />
                            <label className={addressForm.name ? "active" : ""}>Full Name</label>
                        </div>

                        <div className="input-group">
                            <div className="formlabeldiv">
                                <input type="number" required value={addressForm.phone} onChange={(e) => handleInputChange(e, 'phone')} />
                                <label className={addressForm.phone ? "active" : ""}>Phone Contact</label>
                            </div>
                            <div className="formlabeldiv">
                                <select value={addressForm.address_type} onChange={(e) => handleInputChange(e, 'address_type')}>
                                    <option value="home">Home Delivery</option>
                                    <option value="work">Work / Office</option>
                                </select>
                                <label className="active">Address Type</label>
                            </div>
                        </div>

                        <div className="formlabeldiv">
                            <input type="text" required value={addressForm.address_line1} onChange={(e) => handleInputChange(e, 'address_line1')} />
                            <label className={addressForm.address_line1 ? "active" : ""}>Flat / House No / Street Area</label>
                        </div>
                        <div className="formlabeldiv">
                            <input type="text" value={addressForm.address_line2} onChange={(e) => handleInputChange(e, 'address_line2')} />
                            <label className={addressForm.address_line2 ? "active" : ""}>Landmark / Nearby (Optional)</label>
                        </div>

                        <div className="input-group">
                            <div className="formlabeldiv">
                                <select value={addressForm.city} onChange={(e) => handleInputChange(e, 'city')}>
                                    <option value="New Ashok Nagar">New Ashok Nagar</option>
                                    <option value="Noida">Noida</option>
                                    <option value="Ghazipur">Ghazipur</option>
                                </select>
                                <label className="active">City Region</label>
                            </div>
                            <div className="formlabeldiv">
                                <select value={addressForm.state} onChange={(e) => handleInputChange(e, 'state')}>
                                    <option value="Delhi">Delhi</option>
                                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                                </select>
                                <label className="active">State Jurisdiction</label>
                            </div>
                        </div>

                        <div className="formlabeldiv">
                            <input type="text" required value={addressForm.pincode} onChange={(e) => handleInputChange(e, 'pincode')} />
                            <label className={addressForm.pincode ? "active" : ""}>Postal Zip/Pincode</label>
                        </div>
                    </div>
                </div>

                <div className="checkout-sticky-summary">
                    <div className="summary-card-block">
                        <h3>Order Summary</h3>
                        <div className="metrics-row">
                            <span>Payment Type</span>
                            <span><b>{paymentMethod}</b></span>
                        </div>
                        <hr />
                        <div className="metrics-row highlight-total">
                            <span>Total Amount</span>
                            <span>₹{finalGrandTotal.toLocaleString('en-IN')}</span>
                        </div>
                        
                        {error && <p className="error-alert-banner">{error}</p>}
                        
                        <button 
                            type="submit" 
                            disabled={isProcessing || loading || cart.items.length === 0} 
                            className="btn-confirm-order-execution"
                        >
                            {isProcessing || loading ? "Processing Payment..." : "Order Now"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CheckoutPage;